import { FaCheckCircle, FaFileDownload, FaFileExport, FaSpinner } from "react-icons/fa";
import "./TableActions.css"
import UserFileService from "../../Services/UserFileService";
import UserFile from "../../Models/UserFile";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { useState } from "react";

interface TableActionsProps {
    selectedFiles: UserFile[];
    filtredFiles: UserFile[];
    files: UserFile[];
    handleDocumentsDownload: () => Promise<void>;
    setFiles: React.Dispatch<React.SetStateAction<UserFile[]>>;
    setTokenExpired: React.Dispatch<React.SetStateAction<boolean>>;
}

const TableActions: React.FC<TableActionsProps> = ({
    selectedFiles,
    filtredFiles,
    files,
    handleDocumentsDownload,
    setFiles,
    setTokenExpired
}) => {
    // Get User Token
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Wrapper for download with error handling
    const handleDownloadWithErrorHandling = async () => {
        if (!selectedFiles || selectedFiles.length === 0) {
            console.warn("Aucun fichier sélectionné.");
            setError("Veuillez sélectionner au moins un fichier à télécharger.");
            return;
        }
        
        setLoadingDownload(true);
        setError(null);
        
        try {
            await handleDocumentsDownload();
        } catch (e: any) {
            console.error("Erreur lors du téléchargement :", e);
            
            // Provide specific error messages based on the error type
            let errorMessage = "Erreur lors du téléchargement des fichiers. Veuillez réessayer.";
            
            if (e.status === 404) {
                errorMessage = "Un ou plusieurs fichiers sont introuvables.";
            } else if (e.status === 403) {
                errorMessage = "Vous n'avez pas les permissions pour télécharger ces fichiers.";
            } else if (e.status === 500) {
                errorMessage = "Erreur serveur lors du téléchargement. Veuillez réessayer plus tard.";
            } else if (e.message && e.message.includes("vide")) {
                errorMessage = "Le fichier téléchargé est vide ou corrompu.";
            } else if (!navigator.onLine) {
                errorMessage = "Pas de connexion internet. Vérifiez votre connexion.";
            }
            
            setError(errorMessage);
        } finally {
            setLoadingDownload(false);
        }
    };

    // Export current list
    const handleListeExport = () => {
        if (loading) return;
        
        if (!filtredFiles || filtredFiles.length === 0) {
            console.warn("Aucun fichier à exporter.");
            setError("Aucune donnée à exporter. La liste est vide.");
            return;
        }
        
        setLoading(true);
        setError(null);

        // Use setTimeout to allow React to render the loading state
        setTimeout(() => {
            try {
                const columns = [
                    { label: "Format", key: "fileType" },
                    { label: "Nom du fichier", key: "name" },
                    { label: "Date de création", key: "createdAt" },
                    { label: "Date d'échéance", key: "dueDate" },
                    { label: "Type de doc", key: "documentType" },
                    { label: "Entité", key: "entityType" },
                    { label: "Magasin", key: "storeName" },
                    { label: "Statut", key: "isRead" }
                ];

                // Utilisation d'une boucle for pour éviter les problèmes de pile
                const data: Record<string, string | number>[] = [];
                for (let i = 0; i < filtredFiles.length; i++) {
                    const file = filtredFiles[i];
                    const row: Record<string, string | number> = {};
                    for (let j = 0; j < columns.length; j++) {
                        const col = columns[j];
                        let value: string | number;
                        switch (col.key) {
                            case "createdAt":
                                value = file.createdAt ? new Date(file.createdAt).toLocaleDateString("fr-FR") : "";
                                break;
                            case "dueDate":
                                value = file.dueDate ? new Date(file.dueDate).toLocaleDateString("fr-FR") : "-";
                                break;
                            case "isRead":
                                value = file.isRead ? "Lu" : "Non Lu";
                                break;
                            default:
                                value = typeof (file as any)[col.key] === "object" ? JSON.stringify((file as any)[col.key]) : (file as any)[col.key] ?? "";
                        }
                        row[col.label] = value;
                    }
                    data.push(row);
                }

                if (data.length === 0) {
                    throw new Error("Aucune donnée valide à exporter.");
                }

                const worksheet = XLSX.utils.json_to_sheet(data);
                worksheet["!cols"] = columns.map(col => ({ wch: col.label.length + 5 }));

                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Liste");

                const filename = `Liste_${new Date().toLocaleString("fr-FR").replace(/[/:]/g, "_")}.xlsx`;
                XLSX.writeFile(workbook, filename);
            } catch (e: any) {
                console.error("Erreur lors de l'exportation de la liste :", e);
                
                let errorMessage = "Erreur lors de l'exportation de la liste.";
                
                if (e.message && e.message.includes("Aucune donnée")) {
                    errorMessage = e.message;
                } else if (e.message && e.message.includes("quota")) {
                    errorMessage = "Espace de stockage insuffisant pour enregistrer le fichier.";
                } else {
                    errorMessage = "Impossible de créer le fichier Excel. Veuillez réessayer.";
                }
                
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        }, 100); // 100ms delay to allow React to render
    };


    // Change selected documents status
    const handleChangeStatus = async () => {
        if (!selectedFiles || selectedFiles.length === 0) {
            console.warn("Aucun fichier sélectionné.");
            setError("Veuillez sélectionner au moins un fichier pour changer son statut.");
            return;
        }
        
        setLoadingStatus(true);
        setError(null);
        
        try {
            const readCount = selectedFiles.filter(f => f.isRead).length;
            const unreadCount = selectedFiles.length - readCount;

            // If there are more unread files mark all as read 
            // otherwise mark all as unread
            const changeTo = readCount <= unreadCount;

            const filesToChange = selectedFiles.map(file => {
                file.isRead = changeTo;
                return file;
            });
            await UserFileService.changeFilesStatus(filesToChange, token);

            setFiles(files.map((file) => {
                if (selectedFiles.includes(file))
                    file.isRead = changeTo;
                return file;
            }));
        } catch (e: any) {
            console.error("Erreur lors du changement de statut :", e);
            
            if (e.status === 401 && files.length !== 0) {
                setTokenExpired(true);
            } else {
                // Provide specific error messages
                let errorMessage = "Erreur lors du changement de statut. Veuillez réessayer.";
                
                if (e.status === 404) {
                    errorMessage = "Un ou plusieurs fichiers sont introuvables.";
                } else if (e.status === 403) {
                    errorMessage = "Vous n'avez pas les permissions pour modifier ces fichiers.";
                } else if (e.status === 500) {
                    errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
                } else if (!navigator.onLine) {
                    errorMessage = "Pas de connexion internet. Vérifiez votre connexion.";
                }
                
                setError(errorMessage);
            }
        } finally {
            setLoadingStatus(false);
        }
    };

    return (
        <div className="Table-Actions">
            {error && (
                <div className="Table-Actions-Error-Modal">
                    <div className="Table-Actions-Error-Content">
                        <span>{error}</span>
                        <button
                            className="Table-Actions-Error-Close"
                            onClick={() => setError(null)}
                            aria-label="Fermer"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
            {/* Download documents */}
            <button 
                className={`Table-Actions-Button ${selectedFiles.length === 0 || loadingDownload ? 'disabled' : ''}`} 
                onClick={handleDownloadWithErrorHandling}
                disabled={selectedFiles.length === 0 || loadingDownload}
            >
                {loadingDownload ? <FaSpinner className="Table-Actions-Button-Icone spinning" /> : <FaFileDownload className="Table-Actions-Button-Icone" />}
                <span className="Table-Actions-Button-Text">Télécharger la sélection</span>
            </button>
            {/* Change documents status */}
            <button 
                className={`Table-Actions-Button ${selectedFiles.length === 0 || loadingStatus ? 'disabled' : ''}`} 
                onClick={handleChangeStatus}
                disabled={selectedFiles.length === 0 || loadingStatus}
            >
                {loadingStatus ? <FaSpinner className="Table-Actions-Button-Icone spinning" /> : <FaCheckCircle className="Table-Actions-Button-Icone" />}
                <span className="Table-Actions-Button-Text">Changer le statut (lu/non lu)</span>
            </button>
            {/* Export the documents list */}
            <button className={`Table-Actions-Button ${loading ? 'disabled' : ''}`} onClick={handleListeExport} disabled={loading}>
                {loading ? <FaSpinner className="Table-Actions-Button-Icone spinning" /> : <FaFileExport className="Table-Actions-Button-Icone" />}
                <span className="Table-Actions-Button-Text">Imprimer et exporter la liste</span>
            </button>
        </div>
    )
}

export default TableActions;